import React, { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useIntl } from 'react-intl';
import { isEmpty } from 'lodash';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronRight, faCopy } from '@fortawesome/free-solid-svg-icons';

import { FunctionalRootState, getSelectedCurrency, Toaster } from '@share/utils';

import { WalletDetailsModal } from '../../../common/wallet-details-modal';
import { HeaderWalletRewardMessage } from '../../../header/header-wallet-reward-message';
import { useAccountBalances } from '../../balances';
import { SECTION_ICONS } from '../../icons';
import type { AccountSectionId, IAccountSection } from '../../types';
import { getAccountSectionPath, getAccountSections, getHubThemeStyle, isAccountTravelAgent } from '../../utils';

import styles from './styles.module.scss';

interface IProps {
  /** The member's name as the header shows it. */
  memberName?: string;
  /** Closes the drawer; called on every link. */
  onNavigate: () => void;
  /** Mobile drawer layout: name and ID on one row, balances side by side. */
  compact?: boolean;
}

interface IShortcut {
  key: string;
  section: IAccountSection;
  label: string;
  icon: AccountSectionId;
}

/**
 * The account part of the header's hamburger menu for hub tenants: member name and ID, the same
 * balance cards as the My Account Overview (smaller), and shortcuts into My Account sections.
 * Everything else in the drawer (tenant links, gift card, log out) stays in the header.
 */
export const AccountMenuDrawerContent = ({ memberName, onNavigate, compact = false }: IProps): React.ReactElement | null => {
  const intl = useIntl();
  const account = useSelector((state: FunctionalRootState) => state.loginStore.account);
  const user = useSelector((state: FunctionalRootState) => state.loginStore.user);
  const items = useSelector((state: FunctionalRootState) => state.navigationMenuStore.items);
  const [isWalletDetailsOpen, setIsWalletDetailsOpen] = useState(false);

  const { showWallet, wallet, formatWallet, points, pointsLabel, showCommissions, commissions, commissionsRange, formatUsd } = useAccountBalances();

  const sections = useMemo(() => getAccountSections(account, items), [account, items]);
  const accountUsername = account?.name || '';
  const isAgent = isAccountTravelAgent(account, items);
  const memberId = String(items?.rsiId || user?.keyid || '');

  const sectionById = (id: AccountSectionId) => sections.find(section => section.id === id);
  const sectionLabel = (section: IAccountSection) => section.label || intl.formatMessage({ id: section.labelId });

  // A short list: home, bookings, saved properties (when the member has them), profile. Agents also get My reports.
  const shortcuts: IShortcut[] = [];
  const overview = sectionById('overview');
  if (overview) {
    shortcuts.push({ key: 'overview', section: overview, label: intl.formatMessage({ id: isAgent ? 'my.dashboard' : 'my.account.title' }), icon: 'overview' });
  }
  const reports = sectionById('reports');
  if (reports) {
    shortcuts.push({ key: 'reports', section: reports, label: sectionLabel(reports), icon: 'reports' });
  }
  const bookings = sectionById('bookings') || sectionById('history');
  if (bookings) {
    shortcuts.push({ key: 'bookings', section: bookings, label: sectionLabel(bookings), icon: bookings.id });
  }
  const saved = sectionById('saved');
  if (saved) {
    shortcuts.push({ key: 'saved', section: saved, label: sectionLabel(saved), icon: 'saved' });
  }
  const profile = sectionById('profile');
  if (profile) {
    shortcuts.push({
      key: 'profile',
      section: profile,
      label: intl.formatMessage({ id: sectionById('password') ? 'my.account.hub.menu.profile_password' : 'profile' }),
      icon: 'profile',
    });
  }

  const overviewPath = overview ? getAccountSectionPath(accountUsername, overview) : `/${accountUsername}`;
  const reportsPath = reports ? getAccountSectionPath(accountUsername, reports) : overviewPath;

  const handleCopyMemberId = () => {
    if (!navigator.clipboard) {
      Toaster.error(intl.formatMessage({ id: 'my.account.hub.member_id_copy_failed' }));
      return;
    }
    navigator.clipboard.writeText(memberId)
      .then(() => Toaster.success(intl.formatMessage({ id: 'my.account.hub.member_id_copied' })))
      .catch((error: unknown) => {
        console.error('Account menu: copying the member ID failed', error);
        Toaster.error(intl.formatMessage({ id: 'my.account.hub.member_id_copy_failed' }));
      });
  };

  const clientCashName = isEmpty(account?.walletClientCashName) ? 'Client Cash' : account?.walletClientCashName;
  const walletCurrency = !isEmpty(wallet?.label) ? wallet?.label : getSelectedCurrency(account);

  return (
    <div className={compact ? `${styles.menu} ${styles.compact}` : styles.menu} style={getHubThemeStyle(account)}>
      <div className={styles.identity}>
        {memberName ? <span className={styles.name}>{memberName}</span> : null}
        {isAgent && items?.email ? <span className={styles.email}>{items.email}</span> : null}
        {memberId ? (
          <span className={styles.memberId}>
            <span className={styles.memberIdLabel}>{intl.formatMessage({ id: 'my.account.hub.member_id_label' })}</span>
            <span className={styles.memberIdValue}>{memberId}</span>
            <button
              type="button"
              className={styles.copy}
              onClick={handleCopyMemberId}
              aria-label={intl.formatMessage({ id: 'my.account.hub.member_id_copy' }, { id: memberId })}
            >
              <FontAwesomeIcon icon={faCopy} aria-hidden="true" />
            </button>
          </span>
        ) : null}
      </div>

      {showCommissions || showWallet || points !== null ? (
        <div className={styles.cards}>
          {showCommissions ? (
            <Link to={reportsPath} onClick={onNavigate} className={`${styles.card} ${styles.cardCommissions}`}>
              <span className={styles.cardText}>
                <span className={styles.cardLabel}>
                  {intl.formatMessage({ id: 'my.account.hub.commissions.card_title' }, { range: commissionsRange })}
                </span>
                <span className={styles.cardValue}>{commissions ? formatUsd(commissions.commissions) : '—'}</span>
                {commissions ? (
                  <span className={styles.cardNote}>
                    {intl.formatMessage({ id: 'my.account.hub.menu.commissions_note' }, { amount: formatUsd(commissions.pendingCommissions) })}
                  </span>
                ) : null}
              </span>
              <FontAwesomeIcon icon={faChevronRight} className={styles.cardChevron} aria-hidden="true" />
            </Link>
          ) : null}

          {showWallet ? (
            <Link to={overviewPath} onClick={onNavigate} className={`${styles.card} ${styles.cardWallet}`}>
              <span className={styles.cardText}>
                <span className={styles.cardLabel}>{intl.formatMessage({ id: 'my.account.hub.wallet.title' })}</span>
                <span className={styles.cardValue}>{formatWallet(wallet?.balance)}</span>
                {wallet?.pendingBalance ? (
                  <span className={styles.cardNote}>
                    {intl.formatMessage({ id: 'my.account.hub.menu.pending' }, { amount: formatWallet(wallet.pendingBalance) })}
                  </span>
                ) : null}
              </span>
              <FontAwesomeIcon icon={faChevronRight} className={styles.cardChevron} aria-hidden="true" />
            </Link>
          ) : null}

          {points !== null ? (
            <Link to={overviewPath} onClick={onNavigate} className={`${styles.card} ${styles.cardPoints}`}>
              <span className={styles.cardText}>
                <span className={styles.cardLabel}>{pointsLabel}</span>
                <span className={styles.cardValue}>{intl.formatNumber(points)}</span>
                <span className={styles.cardNote}>{intl.formatMessage({ id: 'my.account.hub.menu.points_note' })}</span>
              </span>
              <FontAwesomeIcon icon={faChevronRight} className={styles.cardChevron} aria-hidden="true" />
            </Link>
          ) : null}

          {showWallet ? (
            <button type="button" className={styles.detailsLink} onClick={() => setIsWalletDetailsOpen(true)}>
              {intl.formatMessage({ id: 'wallet.header.view_details' })}
            </button>
          ) : null}
        </div>
      ) : null}

      <HeaderWalletRewardMessage onSeeAllClick={onNavigate} />

      {shortcuts.length ? (
        <nav className={styles.shortcuts} aria-label={intl.formatMessage({ id: 'my.account.hub.menu.shortcuts' })}>
          {shortcuts.map(shortcut => (
            <Link
              key={shortcut.key}
              to={getAccountSectionPath(accountUsername, shortcut.section)}
              onClick={onNavigate}
              className={styles.shortcut}
            >
              <FontAwesomeIcon icon={SECTION_ICONS[shortcut.icon]} className={styles.shortcutIcon} aria-hidden="true" />
              <span className={styles.shortcutLabel}>{shortcut.label}</span>
              <FontAwesomeIcon icon={faChevronRight} className={styles.shortcutChevron} aria-hidden="true" />
            </Link>
          ))}
        </nav>
      ) : null}

      {showWallet ? (
        <WalletDetailsModal
          isOpen={isWalletDetailsOpen}
          clientCashName={clientCashName}
          currency={walletCurrency}
          onClose={() => setIsWalletDetailsOpen(false)}
        />
      ) : null}
    </div>
  );
};
